import { useCallback, useEffect, useState } from 'react'

/**
 * Row density, persisted.
 *
 * Sets `data-density` on <html>. The attribute is the contract; the compact
 * rules are per-app, because what compacts is whatever that app puts in rows.
 * Write them against your own row classes in one block:
 *
 *   [data-density="compact"] .issue-row  { padding-block: 0.35rem; }
 *   [data-density="compact"] .board-card { padding: 0.5rem 0.65rem; }
 *
 * Pass `storageKey` so two tools on the same origin do not share one setting.
 */

export type Density = 'comfortable' | 'compact'

const DEFAULT_STORAGE_KEY = 'boldfit-density'

function readStored(key: string): Density {
  try {
    const stored = localStorage.getItem(key)
    if (stored === 'comfortable' || stored === 'compact') return stored
  } catch {
    /* fall through to the default */
  }
  return 'comfortable'
}

function writeStored(key: string, density: Density) {
  try {
    localStorage.setItem(key, density)
  } catch {
    /* preference is not persisted; the session still works */
  }
}

function applyDensity(density: Density) {
  document.documentElement.setAttribute('data-density', density)
}

export function useDensity({ storageKey = DEFAULT_STORAGE_KEY }: { storageKey?: string } = {}) {
  /* Applied in the initialiser so the first paint is already at the right
     density rather than reflowing every row after mount. */
  const [density, setDensity] = useState<Density>(() => {
    const stored = readStored(storageKey)
    applyDensity(stored)
    return stored
  })

  useEffect(() => {
    applyDensity(density)
    writeStored(storageKey, density)
  }, [density, storageKey])

  const toggleDensity = useCallback(() => {
    setDensity((prev) => (prev === 'comfortable' ? 'compact' : 'comfortable'))
  }, [])

  return { density, setDensity, toggleDensity }
}
